<template>
  <div class="project-page">
    <ProjectHero :project="project" />
    <FeatureGrid :features="project.features" :color="project.color" subtitle="多路预览、逐帧 Bitmap、拍照录像、推流，Android UVC 摄像头完整方案" />

    <CodeSection
      :code-blocks="project.codeBlocks"
      subtitle="引入模块、权限布局、Kotlin 与 Java 最小代码，快速集成"
    />

    <!-- 注意事项 -->
    <section class="section notes-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">IMPORTANT</span>
          <h2 class="section-title">注意事项（必读）</h2>
          <p class="section-subtitle">集成前请仔细阅读以下要点，避免常见踩坑</p>
        </div>
        <div class="notes-grid">
          <div
            v-for="(n, i) in project.notes"
            :key="n.title"
            class="note-card"
            data-aos="fade-up"
            :data-aos-delay="(i % 2) * 80"
          >
            <div class="note-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div>
              <h3 class="note-title">{{ n.title }}</h3>
              <p class="note-desc">{{ n.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 工程结构 -->
    <section class="section struct-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">STRUCTURE</span>
          <h2 class="section-title">工程结构</h2>
          <p class="section-subtitle">库模块 yuvc 对外 API 为 com.yujing.uvc.YUVC，含 JNI native 层</p>
        </div>
        <div class="struct-box" data-aos="fade-up" data-aos-delay="80">
          <pre><code>{{ project.structure }}</code></pre>
        </div>
      </div>
    </section>

    <section class="section" style="padding-top:0">
      <div class="container">
        <div class="proj-cta-box" data-aos="fade-up">
          <div>
            <h3 class="proj-cta-title">库模块详细 API</h3>
            <p class="proj-cta-desc">详见 yuvc/README.md，前往 Gitee 查看完整源码与文档。</p>
          </div>
          <a :href="project.repo" target="_blank" rel="noopener" class="btn btn-primary">
            <i class="fa-brands fa-git-alt"></i> 前往 Gitee
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { projects } from '@/data/projects.js'
import ProjectHero from '@/components/ProjectHero.vue'
import FeatureGrid from '@/components/FeatureGrid.vue'
import CodeSection from '@/components/CodeSection.vue'

const project = projects.yuvc
</script>

<style scoped>
.notes-section { border-top: 1px solid var(--border-soft); }
.notes-grid {
  margin-top: 56px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}
.note-card {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 22px 24px;
  transition: all 0.25s ease;
}
.note-card:hover {
  border-color: var(--border);
  transform: translateY(-3px);
}
.note-num {
  font-family: var(--font-mono);
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--accent);
  opacity: 0.6;
  line-height: 1;
}
.note-title {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.05rem;
  margin-bottom: 6px;
}
.note-desc { color: var(--text-muted); font-size: 0.88rem; line-height: 1.6; }

.struct-section { border-top: 1px solid var(--border-soft); }
.struct-box {
  margin-top: 48px;
  background: #0d1117;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 28px 30px;
  overflow-x: auto;
}
.struct-box pre {
  font-family: var(--font-mono);
  font-size: 0.86rem;
  line-height: 1.8;
  color: #c9d1d9;
}
.struct-box code { font-family: inherit; }

.proj-cta-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  padding: 36px 40px;
}
.proj-cta-title { font-family: var(--font-display); font-weight: 600; font-size: 1.5rem; margin-bottom: 6px; }
.proj-cta-desc { color: var(--text-muted); }

@media (max-width: 768px) { .notes-grid { grid-template-columns: 1fr; } }
</style>
